import type { CombinedError } from "urql";
import { toast } from "@/components/ui/toast";
import { apiEndpointBaseUrl } from "@/data-access/core/constants";
import {
  ExchangeSso,
  Login,
  Logout,
  StartSso,
} from "@/data-access/user/queries/auth-queries";
import {
  readSsoReturnParams,
  resolveSsoErrorMessage,
  SSO_NOT_AVAILABLE_MESSAGE,
  stripSsoReturnParamsFromUrl,
} from "@/lib/auth/ssoReturnUtils";
import { client, resetClient } from "@/lib/urqlProvider";
import { defaultUserInfo, type UserInfo } from "@/types/user/userInfo";
import { fetchMe, profileLoadFailedMessage } from "./auth.actions";
import useAuth from "./auth.slice";
import { endSession } from "./sessionTeardown";

const errorMessages = {
  invalidCredentials:
    "The username or password you entered is incorrect. Please try again.",
  loginFailed: "Something went wrong signing you in. Please try again.",
  networkError: "Network error, please try again.",
  failedRegistration:
    "We encountered an issue while creating your account. Please try again later.",
  googleLoginFailed:
    "There was an issue logging in with Google. Please retry or use a different login method.",
  resetPasswordFailed: "An error occurred. Please try again later.",
  resetPassConfirmFailed: "An error occurred. Please try again later.",
} as const;

export interface LoginPayload {
  email: string;
  password: string;
}

export interface GoogleLoginPayload {
  client_id: string;
  access_token: string;
}

export interface RegisterPayload {
  email: string;
  password1: string;
  password2: string;
}

export interface ResetPasswordConfirmPayload {
  uid: string | null;
  token: string | null;
  new_password1: string;
  new_password2: string;
}

interface AuthResponse {
  user?: UserInfo;
  access?: string;
  refresh?: string;
  detail?: string;
  email?: string[];
  non_field_errors?: string[];
}

interface SafeResponse {
  ok: boolean;
  status?: number;
  networkError?: boolean;
  json: () => Promise<unknown>;
}

const isInvalidCredentialsError = (
  error: CombinedError | undefined,
): boolean => {
  if (!error?.graphQLErrors?.length) {
    return false;
  }

  return error.graphQLErrors.some((graphQLError) => {
    const code = (graphQLError.extensions as { code?: string } | undefined)
      ?.code;
    return (
      code === "INVALID_CREDENTIALS" ||
      /invalid email or password/i.test(graphQLError.message)
    );
  });
};

const getGraphqlErrorCode = (
  error: CombinedError | undefined,
): string | undefined =>
  error?.graphQLErrors?.[0]?.extensions?.code as string | undefined;

const resolveStartSsoErrorMessage = (
  error: CombinedError | undefined,
): string => {
  switch (getGraphqlErrorCode(error)) {
    case "SSO_NOT_AVAILABLE": {
      return SSO_NOT_AVAILABLE_MESSAGE;
    }

    case "SSO_INPUT_REQUIRED": {
      return "Enter your company email to continue with SSO.";
    }
    default: {
      return "Something went wrong starting SSO. Please try again.";
    }
  }
};

const resolveExchangeSsoErrorMessage = (
  error: CombinedError | undefined,
): string => {
  switch (getGraphqlErrorCode(error)) {
    case "INVALID_SSO_CODE": {
      return "This sign-in link has already been used or expired. Please sign in again.";
    }
    default: {
      return "Something went wrong finishing sign-in. Please try again.";
    }
  }
};

const isAuthResponse = (data: unknown): data is AuthResponse => {
  return (
    !!data &&
    typeof data === "object" &&
    ("access" in data || "user" in data || "detail" in data)
  );
};

const safeFetch = async (
  url: string,
  options: RequestInit,
): Promise<SafeResponse> => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    console.error("Error in fetch:", error);
    return {
      ok: false,
      networkError: true,
      json: async () => ({ error: "Fetch failed" }),
    };
  }
};

const createAuthRequest = (endpoint: string, payload: object) => ({
  url: `${apiEndpointBaseUrl}/auth/${endpoint}/`,
  options: {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  },
});

const handleAuthError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  console.error(error);
  toast.error(message);
  useAuth.setState({ status: "failed", error: message });
};

/** Drops persisted tokens so SSO mutations do not send a stale bearer. */
const clearStaleAuthForSsoReturn = () => {
  useAuth.setState({
    accessToken: null,
    refreshToken: null,
    loggedIn: false,
    user: defaultUserInfo,
    isEmployee: false,
    error: null,
  });
};

const setAuthTokens = (accessToken: string, refreshToken: string) => {
  useAuth.setState({
    accessToken,
    refreshToken,
    loggedIn: true,
    user: defaultUserInfo,
    isEmployee: false,
    error: null,
  });
};

const setAuthTokensFromRestResponse = (body: AuthResponse) => {
  useAuth.setState({
    accessToken: body.access ?? null,
    refreshToken: body.refresh ?? null,
    loggedIn: Boolean(body.access),
    user: defaultUserInfo,
    isEmployee: false,
    error: null,
  });
};

const completeAuthentication = async () => {
  const result = await fetchMe();
  if (result !== "success") {
    useAuth.setState({
      accessToken: null,
      refreshToken: null,
      loggedIn: false,
      user: defaultUserInfo,
      isEmployee: false,
    });
    throw new Error(profileLoadFailedMessage);
  }

  useAuth.setState({ status: "succeeded", error: null });
};

const authenticateAfterRest = async (body: AuthResponse) => {
  setAuthTokensFromRestResponse(body);

  if (!body.access) {
    useAuth.setState({ status: "succeeded", error: null });
    return;
  }

  await completeAuthentication();
};

let isLoggingOut = false;

export const logoutUser = async () => {
  if (isLoggingOut) {
    console.log("Logout already in progress");
    return;
  }
  isLoggingOut = true;

  try {
    const token = useAuth.getState().accessToken;
    const userId = useAuth.getState().user?.id;
    console.log(`Starting logout for user ${userId}`);

    if (token) {
      try {
        const result = await client.mutation(Logout, {}).toPromise();
        if (result.error) {
          console.warn("Logout mutation failed:", result.error);
        }
      } catch (error) {
        console.error("Error when calling logout mutation:", error);
      }
    }

    const resetSuccess = await resetClient();
    if (!resetSuccess) {
      console.warn("URQL client reset may not have completed successfully");
    }

    endSession();

    console.log("Logout completed successfully");
  } catch (error) {
    console.error("Error during logout cleanup:", error);
    endSession();
  } finally {
    isLoggingOut = false;
  }
};

export const startLogin = async ({ email, password }: LoginPayload) => {
  useAuth.setState({ status: "loading", error: null });
  try {
    let result = await client.mutation(Login, { email, password }).toPromise();
    if (result.error?.networkError) {
      result = await client.mutation(Login, { email, password }).toPromise();
    }

    const tokens = result.data?.login?.tokens;
    if (!tokens?.accessToken) {
      if (result.error?.networkError) {
        throw new Error(errorMessages.networkError, { cause: result.error });
      }
      if (isInvalidCredentialsError(result.error)) {
        throw new Error(errorMessages.invalidCredentials);
      }
      // Carry the real error as `cause` so handleAuthError logs it once, not twice.
      throw new Error(errorMessages.loginFailed, { cause: result.error });
    }

    try {
      await resetClient();
    } catch (error) {
      console.error("Error during client reset:", error);
    }

    setAuthTokens(tokens.accessToken, tokens.refreshToken);
    await completeAuthentication();
  } catch (error) {
    handleAuthError(error);
  }
};

export const startGoogleLogin = async (payload: GoogleLoginPayload) => {
  useAuth.setState({ status: "loading" });
  try {
    const { url, options } = createAuthRequest("gauth", payload);
    let resp = await safeFetch(url, options);
    if (resp.networkError) {
      resp = await safeFetch(url, options);
    }

    if (resp.networkError) {
      throw new Error(errorMessages.networkError);
    }

    const body = await resp.json();

    // A non-OK status is an expected backend rejection (e.g. registration
    // disabled for the account's domain), so surface the friendly message
    // without logging the raw response body as an unexpected error.
    if (!resp.ok) {
      throw new Error(errorMessages.googleLoginFailed);
    }

    if (!isAuthResponse(body)) {
      console.error("Unexpected Google login response:", body);
      throw new Error(errorMessages.googleLoginFailed);
    }

    await resetClient();
    await authenticateAfterRest(body);
  } catch (error) {
    handleAuthError(error);
  }
};

export const startRegister = async (payload: RegisterPayload) => {
  useAuth.setState({ status: "loading" });
  try {
    const { url, options } = createAuthRequest("registration", payload);
    const resp = await safeFetch(url, options);
    const body = await resp.json();

    if (!isAuthResponse(body)) {
      throw new Error(errorMessages.failedRegistration);
    }

    if (resp.ok) {
      await resetClient();
      await authenticateAfterRest(body);
    } else {
      console.error("Error Registering", body.email, body.non_field_errors);
      throw new Error(errorMessages.failedRegistration);
    }
  } catch (error) {
    handleAuthError(error);
  }
};

export const startResetPassword = async (email: string) => {
  useAuth.setState({ status: "loading" });
  try {
    const { url, options } = createAuthRequest("password/reset", { email });
    const resp = await safeFetch(url, options);
    const body = await resp.json();

    if (!isAuthResponse(body)) {
      throw new Error(errorMessages.resetPasswordFailed);
    }

    if (resp.ok) {
      toast.success(body.detail);
      useAuth.setState({ status: "succeeded", error: null });
    } else {
      throw new Error(errorMessages.resetPasswordFailed);
    }
  } catch (error) {
    handleAuthError(error);
  }
};

export const resetPasswordConfirm = async (
  payload: ResetPasswordConfirmPayload,
) => {
  useAuth.setState({ status: "loading" });
  try {
    const { url, options } = createAuthRequest(
      "password/reset/confirm",
      payload,
    );
    const resp = await safeFetch(url, options);
    const body = await resp.json();

    if (!isAuthResponse(body)) {
      throw new Error(errorMessages.resetPassConfirmFailed);
    }

    if (resp.ok) {
      toast.success(body.detail);
      useAuth.setState({ status: "succeeded", error: null });
    } else {
      throw new Error(errorMessages.resetPassConfirmFailed);
    }
  } catch (error) {
    handleAuthError(error);
  }
};

export const startSso = async (email: string) => {
  useAuth.setState({ status: "loading", error: null });

  try {
    let result = await client.mutation(StartSso, { email }).toPromise();

    if (result.error?.networkError) {
      result = await client.mutation(StartSso, { email }).toPromise();
    }

    const startUrl = result.data?.startSso?.startUrl;

    if (!startUrl) {
      if (result.error?.networkError) {
        throw new Error(errorMessages.networkError, { cause: result.error });
      }

      throw new Error(resolveStartSsoErrorMessage(result.error), {
        cause: result.error,
      });
    }

    window.location.assign(startUrl);
  } catch (error) {
    handleAuthError(error);
  }
};

let isExchangingSsoCode = false;

export const exchangeSsoCode = async (code: string) => {
  if (isExchangingSsoCode) {
    return;
  }
  isExchangingSsoCode = true;

  useAuth.setState({ status: "loading", error: null });

  try {
    let result = await client.mutation(ExchangeSso, { code }).toPromise();

    if (result.error?.networkError) {
      result = await client.mutation(ExchangeSso, { code }).toPromise();
    }

    const tokens = result.data?.exchangeSsoCode?.tokens;

    if (!tokens?.accessToken) {
      if (result.error?.networkError) {
        throw new Error(errorMessages.networkError, { cause: result.error });
      }

      throw new Error(resolveExchangeSsoErrorMessage(result.error), {
        cause: result.error,
      });
    }

    try {
      await resetClient();
    } catch (error) {
      console.error("Error during client reset:", error);
    }

    setAuthTokens(tokens.accessToken, tokens.refreshToken);

    await completeAuthentication();
  } catch (error) {
    handleAuthError(error);
  } finally {
    isExchangingSsoCode = false;
  }
};

/**
 * Handles a return from the SSO callback: surfaces `?sso_error=`, or exchanges
 * `?sso_code=`. Params are stripped first, so a reload cannot replay the code.
 */
export const handleSsoReturnFromUrl = async () => {
  const { code, error } = readSsoReturnParams(
    window.location.search,
    window.location.hash,
  );
  if (!code && !error) {
    return;
  }

  stripSsoReturnParamsFromUrl();
  clearStaleAuthForSsoReturn();

  if (error) {
    handleAuthError(new Error(resolveSsoErrorMessage(error)));
    return;
  }

  if (code) {
    await exchangeSsoCode(code);
  }
};
